import {
  extractIdFromUrl,
  getCurrentUserFullName,
  getShareDestination,
  trackCreationShare,
} from '@utils/analyticsHelpers';
import QRCode from 'qrcode';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useAuth } from 'react-oidc-context';
import { SHARE_DESTINATIONS } from '../../../../constants/analytics';
import { OidcUser } from '../../../../types/oidcUser';

interface MyCreationsShareTooltipProps {
  isOpen: boolean;
  onClose: () => void;
  trackName: string;
  shareUrl: string;
  title?: string;
  triggerRef?: React.RefObject<HTMLDivElement | null>;
}

export const MyCreationsShareTooltip = ({
  isOpen,
  onClose,
  trackName,
  shareUrl,
  title,
  triggerRef,
}: MyCreationsShareTooltipProps) => {
  const { t } = useTranslation();
  const displayTitle = title || t('share.title');
  const [copySuccess, setCopySuccess] = useState(false);
  const [position, setPosition] = useState<{
    top?: number;
    bottom?: number;
    left?: number;
    showAbove: boolean;
    useCenteredModal: boolean;
  }>({ showAbove: false, useCenteredModal: false });
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Calculate position - use centered modal when neither above nor below has enough room
  const updatePosition = useCallback(() => {
    if (isOpen && triggerRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const estimatedOverlayHeight = 420;

      const fitsBelow = spaceBelow >= estimatedOverlayHeight;
      const fitsAbove = spaceAbove >= estimatedOverlayHeight;

      let showAbove = false;
      let useCenteredModal = false;

      if (fitsBelow) {
        showAbove = false;
      } else if (fitsAbove) {
        showAbove = true;
      } else {
        useCenteredModal = true;
      }

      setPosition({
        top: useCenteredModal
          ? undefined
          : showAbove
            ? undefined
            : rect.bottom + 8,
        bottom: useCenteredModal
          ? undefined
          : showAbove
            ? viewportHeight - rect.top + 8
            : undefined,
        left: useCenteredModal ? undefined : rect.left + rect.width / 2,
        showAbove: useCenteredModal ? false : showAbove,
        useCenteredModal,
      });
    }
  }, [isOpen, triggerRef]);

  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Generate QR code when tooltip opens
  useEffect(() => {
    if (isOpen && shareUrl && !qrCodeDataUrl) {
      QRCode.toDataURL(shareUrl, {
        width: 200,
        margin: 2,
        errorCorrectionLevel: 'H',
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      })
        .then((dataUrl) => {
          setQrCodeDataUrl(dataUrl);
        })
        .catch((err) => {
          console.error('Failed to generate QR code:', err);
        });
    }
  }, [isOpen, shareUrl, qrCodeDataUrl]);

  // Close tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleScroll = () => {
      onClose();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, onClose]);

  const shareText = t('share.shareText', { title: trackName });

  const contentId = extractIdFromUrl(shareUrl);
  const creatorName = getCurrentUserFullName(user as OidcUser | null);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);

      // Analytics: creation_share (Copy Link)
      trackCreationShare({
        contentId,
        contentType: 'Music',
        contentName: trackName,
        creatorName,
        shareDestination: SHARE_DESTINATIONS.COPY_LINK,
      });
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  }, [shareUrl, contentId, trackName, creatorName]);

  const handleSocialShare = useCallback(
    (platform: string) => {
      const encodedText = encodeURIComponent(shareText);
      const encodedUrl = encodeURIComponent(shareUrl);

      let shareLink = '';

      switch (platform) {
        case 'facebook':
          shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
          break;
        case 'twitter':
          shareLink = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
          break;
        case 'instagram':
          navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
          alert(
            t('share.instagramCopied'),
          );
          trackCreationShare({
            contentId,
            contentType: 'Music',
            contentName: trackName,
            creatorName,
            shareDestination: SHARE_DESTINATIONS.INSTAGRAM,
          });
          return;
        case 'whatsapp':
          shareLink = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
          break;
        case 'telegram':
          shareLink = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
          break;
        case 'linkedin':
          shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
          break;
        default:
          return;
      }

      if (shareLink) {
        window.open(shareLink, '_blank', 'noopener,noreferrer');
      }
      // Analytics: creation_share (social)
      trackCreationShare({
        contentId,
        contentType: 'Music',
        contentName: trackName,
        creatorName,
        shareDestination: getShareDestination(platform),
      });
      onClose();
    },
    [shareText, shareUrl, onClose, contentId, trackName, creatorName],
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      id="my-creations-share-tooltip"
      style={{
        position: 'fixed',
        ...(position.useCenteredModal
          ? {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }
          : {
              ...(position.top !== undefined && { top: `${position.top}px` }),
              ...(position.bottom !== undefined && {
                bottom: `${position.bottom}px`,
              }),
              left: `${position.left}px`,
              transform: 'translateX(-50%)',
            }),
      }}
      className={`bg-black/90 backdrop-blur-sm border border-white/20 rounded-lg p-3 min-w-[200px] z-[9999] shadow-lg ${
        position.useCenteredModal
          ? 'max-h-[calc(100vh-2rem)] overflow-y-auto'
          : ''
      }`}>
      {!position.useCenteredModal && (
        <div
          className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 ${
            position.showAbove
              ? 'top-full border-t-4 border-l-transparent border-r-transparent border-t-white/20'
              : 'bottom-full border-b-4 border-l-transparent border-r-transparent border-b-white/20'
          }`}
        />
      )}

      <div className="text-white text-sm font-medium mb-3 text-center">
        {displayTitle}
      </div>

      {/* Social Media Buttons */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <button
          onClick={() => handleSocialShare('facebook')}
          className="flex flex-col items-center p-2 hover:bg-white/10 rounded-lg transition-colors"
          title={t('share.shareOnFacebook')}>
          <div className="w-6 h-6 bg-[#1877F2] rounded-full flex items-center justify-center mb-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
          </div>
          <span className="text-xs text-white/80">{t('share.facebook')}</span>
        </button>

        <button
          onClick={() => handleSocialShare('twitter')}
          className="flex flex-col items-center p-2 hover:bg-white/10 rounded-lg transition-colors"
          title={t('share.shareOnTwitter')}>
          <div className="w-6 h-6 bg-[#1DA1F2] rounded-full flex items-center justify-center mb-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417a9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
            </svg>
          </div>
          <span className="text-xs text-white/80">{t('share.twitter')}</span>
        </button>

        <button
          onClick={() => handleSocialShare('instagram')}
          className="flex flex-col items-center p-2 hover:bg-white/10 rounded-lg transition-colors"
          title={t('share.shareOnInstagram')}>
          <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
          </div>
          <span className="text-xs text-white/80">{t('share.instagram')}</span>
        </button>

        <button
          onClick={() => handleSocialShare('whatsapp')}
          className="flex flex-col items-center p-2 hover:bg-white/10 rounded-lg transition-colors"
          title={t('share.shareOnWhatsApp')}>
          <div className="w-6 h-6 bg-[#25D366] rounded-full flex items-center justify-center mb-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
            </svg>
          </div>
          <span className="text-xs text-white/80">{t('share.whatsapp')}</span>
        </button>

        <button
          onClick={() => handleSocialShare('telegram')}
          className="flex flex-col items-center p-2 hover:bg-white/10 rounded-lg transition-colors"
          title={t('share.shareOnTelegram')}>
          <div className="w-6 h-6 bg-[#0088CC] rounded-full flex items-center justify-center mb-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
            </svg>
          </div>
          <span className="text-xs text-white/80">{t('share.telegram')}</span>
        </button>

        <button
          onClick={() => handleSocialShare('linkedin')}
          className="flex flex-col items-center p-2 hover:bg-white/10 rounded-lg transition-colors"
          title={t('share.shareOnLinkedIn')}>
          <div className="w-6 h-6 bg-[#0077B5] rounded-full flex items-center justify-center mb-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </div>
          <span className="text-xs text-white/80">{t('share.linkedin')}</span>
        </button>
      </div>

      {/* QR Code Section */}
      {qrCodeDataUrl && (
        <div className="border-t border-white/20 pt-3 mb-3">
          <div className="text-white/60 text-xs text-center mb-2">
            {t('share.scanToShare')}
          </div>
          <div className="flex justify-center">
            <div className="w-32 h-32 bg-white p-2 rounded-lg">
              <img
                src={qrCodeDataUrl}
                alt={t('share.qrCode')}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {/* Copy Link Button */}
      <div className="border-t border-white/20 pt-3">
        <button
          onClick={handleCopyLink}
          className="w-full flex items-center justify-center gap-2 p-2 hover:bg-white/10 rounded-lg transition-colors">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="text-white/80">
            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
          </svg>
          <span className="text-sm text-white/80">
            {copySuccess ? t('common.copiedLink') : t('common.copyLink')}
          </span>
        </button>
      </div>
    </div>,
    document.body,
  );
};
